import React, {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import {
    useLoaderData,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import { useImmer } from 'use-immer';

import { PaymentProcessor } from '@r6digital/r6pay-types/types/PaymentProcessor';
import { NextAction } from '@r6digital/r6pay-types/types/Requests/PaymentLinkCreate';
import {
    creditCardType,
    validateCreditCardCSV,
    validateCreditCardNumber,
} from '@r6digital/rapidstor-types/react/entities/CreditCard';
import { WizardType } from '@r6digital/rapidstor-types/react/api/Requests';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import { ScaApplicationViews } from '@r6digital/rapidstor-types/enums/sockets';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import {
    LineItem,
    RapidStorBoxShopCouponResponse,
} from '@r6digital/rapidstor-types/types/Merchandise';
import {
    BaseReservation,
    CostRetrieveResponse,
} from '@r6digital/rapidstor-types/types/Reservation';
import { JourneyVariant } from '@r6digital/rapidstor-types/react/entities/Journey';
import {
    useGetCorpQuery,
    useGetMerchandiseQuery,
    useGetMoveInCostRetrieveMutation,
    useGetWizardInitialisationQuery,
    useGetReservationQuery,
    usePatchReservationMutation,
    usePostMoveInMutation,
    usePostScaMoveInBillingInfoUpdateMutation,
    usePostScaMoveInPrepareMutation,
    usePostScaMoveInPaymentMutation,
} from '@/core/api/rapidstor';
import useCart from '@/core/hooks/useCart';
import { useMoveInSocket } from '@/core/hooks/useMoveInSocket';
import { useMoveInUnitAvailability } from '@/core/hooks/useUnitAvailability';
import {
    CorporationCode,
    getCorpTranslations,
} from '@/core/translations/helpers';
import {
    LoqateAddressFindResponseItem,
    LoqateValidationFields,
} from '@/core/types/Loqate';
import { WizardValidationError } from '@/core/types/Wizard';
import { getConfirmButtonText } from '@/core/utils/i18n';
import Logger from '@/core/utils/logging';
import {
    fromNeededDate,
    getDatePickerRange,
    getDefaultNeededDate,
    RapidStorSettings,
} from '@/core/utils/rapidstor';
import { fadeOut } from '@/core/utils/ui';
import {
    excludedInputs,
    normaliseFieldName,
    validatePassword,
    validateString,
    validateEmail,
    validateAutobill,
} from '@/core/utils/validation';
import ErrorComponent from '@/journey-based-listing/screens/error';

import store from '@/store';
import { setPersonaId } from '@/store/bookingSlice';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import CircleCheckbox from '@/core/components/elements/circle-checkbox';
import ErrorTag from '@/core/components/elements/error-tag';
import OAuthIntegrations from '@/core/components/elements/oauth-integrations';
import ReservationUnavailable from '@/core/components/elements/reservation-unavailable';
import Spinner from '@/core/components/elements/spinner';
import WizardDescriptor from '@/core/components/wizard/descriptor';
import AccountDetails from '@/core/components/wizard/forms/account-details';
import AutoBill from '@/core/components/wizard/forms/autobill';
import ContactDetailsForm from '@/core/components/wizard/forms/contact-details';
import IdentityVerification from '@/core/components/wizard/forms/identity-verification';
import InsuranceCoverageComponent from '@/core/components/wizard/forms/insurance-coverage';
import Merchandise from '@/core/components/wizard/forms/merchandise';
import PaymentForm from '@/core/components/wizard/forms/payment-form';
import ProofOfAddress from '@/core/components/wizard/forms/proof-of-address';
import ProofOfInsurance from '@/core/components/wizard/forms/proof-of-insurance';
import ProductRemovalModal from '@/core/components/wizard/modals/product-removal';
import WizardPricingTable from '@/core/components/wizard/pricing-table';
import SimplifiedCheckout from '@/core/components/wizard/simplified-checkout';
import StripePaymentForm from '@/core/components/wizard/forms/stripe-payment-form';

import { deconstructScaErrorMessage, isInsuranceCoverageAvailable, paymentGatewayReturnUrl } from '@/features/rapidstor/helpers';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { useR6PayContext } from '@/features/rapidstor/providers/R6PayServiceProvider';
import { StripePaymentIntent } from '@/features/rapidstor/services/R6PayService';
import LoqateValidator from '@/features/rapidstor/services/LoqateService';
import ReservationService from '@/features/rapidstor/services/ReservationService';

import 'react-datepicker/dist/react-datepicker.css';
import classes from '@/scss/screens/move-in.module.scss';

interface LoqateAddressResults {
    main: LoqateAddressFindResponseItem[];
    alternate: LoqateAddressFindResponseItem[];
}

interface Props {
    oAuthReturn?: boolean;
}

export default function MoveIn(props: Props) {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { token: reservationIdentifier } = useParams();
    const { t, i18n } = useTranslation();
    const r6PayService = useR6PayContext(location);
    const {
        unitList,
        selectionTypes,
        persona,
        setupSliceData,
    } = useContext(JourneyContext);
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const reservationSvc = new ReservationService(location);

    /** @TODO get rid of storing descriptor and unitType here. It's already in the bookingSlice */
    const [descriptor, setDescriptor] = useState<Descriptor>();
    const [unitType, setUnitType] = useState<UnitType>();
    const [reservation, setReservation] = useState<BaseReservation>();
    const [costRetrieve, setCostRetrieve] = useState<CostRetrieveResponse>();
    const [moveInError, setMoveInError] = useState<string>();
    const [moveInLoading, setMoveInLoading] = useState<boolean>(false);
    const [insurancePending, setInsurancePending] = useState<boolean>(false);
    const [stripeIntent, setStripeIntent] = useState<StripePaymentIntent | null>(null);
    const [loqateLoading, setLoqateLoading] = useState<LoqateValidationFields>();
    const [loqateAddressResults, setLoqateAddressResults] = useState<LoqateAddressResults>();
    const [bankName, setBankName] = useState<string>();
    const [useSameAddress, setUseSameAddress] = useState<boolean>(false);
    const [cookies, setCookie] = useCookies([
        'reservation',
        'selectedCustomerType',
    ]);
    const [productRemoval, setProductRemoval] = useState<LineItem | null>(null);

    const [moveInCostRetrieve, { error: moveInCostRetrieveError }] = useGetMoveInCostRetrieveMutation();
    const [makeScaBillingInfoUpdate] = usePostScaMoveInBillingInfoUpdateMutation();
    const [makeScaMoveInPrepare] = usePostScaMoveInPrepareMutation();
    const [makeScaMoveInPayment] = usePostScaMoveInPaymentMutation();
    const [makeMoveIn] = usePostMoveInMutation();

    const [patchReservation] = usePatchReservationMutation();

    const [searchParams] = useSearchParams();

    const { data: res, isSuccess: onSuccess } = useGetReservationQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            token: reservationIdentifier,
        },
        {
            refetchOnMountOrArgChange: true,
        },
    );

    const { data: corporationData } = useGetCorpQuery(
        {
            corpCode: location.corpCode,
        },
        {
            refetchOnMountOrArgChange: false,
        },
    );

    const nowDate = useMemo(
        () => moment(res?.moveInContext?.neededDate || new Date().toISOString()),
        [res],
    );

    const {
        data: moveInWizardDataInit,
        isLoading: moveInInitLoading,
        isSuccess: moveInInitSuccess,
        isError: moveInInitError,
        refetch: refetchMoveInWizardDataInit,
    } = useGetWizardInitialisationQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            wizard: WizardType.MOVE_IN,
            reservationToken: res?.token,
        },
        {
            skip: !onSuccess,
            refetchOnMountOrArgChange: true,
        },
    );

    // SiteLink state of the reservation, read on page load. A cancelled or expired
    // reservation cannot take a payment, so the wizard shows a message instead of the form.
    const blockingStatus = moveInWizardDataInit?.reservationStatus
        && (moveInWizardDataInit.reservationStatus.cancelled
            || moveInWizardDataInit.reservationStatus.expired)
        ? moveInWizardDataInit.reservationStatus
        : null;

    // Use shared unit availability hook
    const { handleUnitAvailability } = useMoveInUnitAvailability(
        location,
        res,
        setReservation,
        setMoveInError,
        setMoveInLoading,
    );

    // Listens for the non-SCA `moveInComplete` socket event (plumbing only — logs the
    // result, does not affect navigation).
    const { startListening: startMoveInSocketListening } = useMoveInSocket({ location });

    // Unit availability, cost retrieve and reservation patches can each update the reservation in
    // SiteLink. SiteLink rejects overlapping updates with a timestamp mismatch, so run them in order.
    const reservationTaskQueue = useRef<Promise<unknown>>(Promise.resolve());
    function enqueueReservationTask<T>(task: () => Promise<T>): Promise<T> {
        const run = reservationTaskQueue.current.then(task);
        reservationTaskQueue.current = run.catch(() => undefined);
        return run;
    }

    useEffect(() => {
        async function unitAvailabilityCheck() {
            if (res?._id && onSuccess) {
                await enqueueReservationTask(handleUnitAvailability);
            }
        }
        unitAvailabilityCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSuccess]);

    const { data: moveInWizardMerchandise, isSuccess: merchandiseSuccess } = useGetMerchandiseQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
        },
        {
            skip: !moveInInitSuccess,
        },
    );

    const availableInsuranceCoverage = useMemo(() => moveInWizardDataInit?.siteInsuranceCoverage.filter(
        (coverage) => isInsuranceCoverageAvailable(coverage, res?.unitType.area),
    ), [moveInWizardDataInit?.siteInsuranceCoverage, res?.unitType.area]);

    const [form, updateForm] = useImmer<Record<string, string>>({
        location: location.locationCode,
        tenant_commercial: (function () {
            if (persona && selectionTypes) {
                const personaData = selectionTypes.find((type) => type._id === persona);
                return personaData?.customerType === 'business' ? 'true' : 'false';
            }
            if (cookies.selectedCustomerType === 'business') {
                return 'true';
            }
            return 'false';
        }()),
        company: '',
        companyNumber: '',
        title: 'Mr',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postcode: '',

        title_alternate: 'Mr',
        firstName_alternate: '',
        lastName_alternate: '',
        email_alternate: '',
        phone_alternate: '',
        address_alternate: '',
        city_alternate: '',
        state_alternate: '',
        postcode_alternate: '',

        driverLicenseNumber: '',
        driverLicenseState: '',
        dateOfBirth: '',
        idDocumentNumber: '',
        licencePlate: '',

        insuranceCoverage: '',

        neededDate: getDefaultNeededDate(location, nowDate).toISOString(),

        termsAndConditionsAgree: 'false',
        marketingAgree: 'false',
        assistanceMovingRequested: 'false',
        declaredValueOfGoodsAgree: 'false',
        insuranceType: 'flatRate',
        declaredValueOfGoods: '',

        pickupLocation: '',

        boxShopCouponId: '',

        password: '',
        confirmPassword: '',

        cardHolderName: '',
        cardNumber: '',
        cardExpiryMonth: '',
        cardExpiryYear: '',
        cardCsv: '',

        autobillAccountNumber: '',
        autobillBsbNumber: '',
        autobillAccountType: '1',

        autobillCreditCardName: '',
        autobillCreditCardNumber: '',
        autobillCreditCardExpiryMonth: '',
        autobillCreditCardExpiryYear: '',
        autobillCreditCardCsv: '',

        autobillType: RapidStorSettings(location).autobill().defaultType(
            window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified',
        ),
        autobillUsePaymentDetails: (function () {
            if (window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified') {
                // Checkbox starts checked only when autobill is on by default.
                return RapidStorSettings(location).autobill().defaultType(true) === 'cc'
                    ? 'true'
                    : 'false';
            }

            // Legacy default: reuse the main payment card for credit-card autobill.
            return 'true';
        }()),
        });

    const fetchCostRetrieve = async () => {
        try {
            const result = await moveInCostRetrieve({
                location: location.locationCode,
                corpCode: location.corpCode,
                reservationId: res?.token,
            }).unwrap();

            return result;
        } catch (error) {
            Logger.error(
                '[fetchCostRetrieve] Failed fetching cost retrieve',
                location,
                [
                    {
                        name: 'rapidstor_reservationToken',
                        value: `${res?.token}`,
                    },
                ],
            );

            return undefined;
        }
    };

    /*
    This is similar in functionality (but without validation) to the one in the ReservationService, but because
    the payload is different and ReservationService is a class, it's not possible to reuse.
    @TODO: refactor the function in ReservationService to be more generic and reusable, or create
    a new function in a more generic location.
    */
    // const buildAutobillObject = (
    //     formData: Record<string, string>,
    // ): MoveInAutoBillPayload => {
    //     let autoBillType = AutoBillType.None;

    //     if (location.rapidstor.useAutoBilling) {
    //         if (formData.autobillType === 'cc') {
    //             autoBillType = AutoBillType.CreditCard;
    //         } else if (formData.autobillType === 'dd') {
    //             autoBillType = AutoBillType.DirectDebit;
    //         } else {
    //             autoBillType = AutoBillType.None;
    //         }
    //     }

    //     const autoBillObject: MoveInAutoBillPayload = {
    //         usePaymentDetails:
    //     !!(formData.autobillUsePaymentDetails === 'true'
    //     && autoBillType === AutoBillType.CreditCard), // Currently only allow card payments for move-in
    //         type: autoBillType,
    //         enabled: autoBillType > AutoBillType.None,
    //     };

    //     // Check if using a payment gateway, if true we don't use autobill
    //     if (location.rapidstor.paymentGateways.use) {
    //         autoBillObject.type = AutoBillType.None;

    //         return autoBillObject;
    //     }

    //     if (autoBillObject.type === AutoBillType.CreditCard) {
    //         Logger.info(
    //             'Using payment credit card details for autobilling',
    //             location,
    //         );
    //         autoBillObject.expiryMonth = formData.cardExpiryMonth;
    //         autoBillObject.expiryYear = moment(formData.cardExpiryYear, 'YYYY').format(
    //             'YYYY',
    //         );
    //         autoBillObject.number = formData.cardNumber;
    //         autoBillObject.cvv = formData.cardCsv;
    //         autoBillObject.name = formData.cardHolderName;
    //     } else if (autoBillObject.type === AutoBillType.DirectDebit) {
    //         Logger.info('Using direct debit details', location);
    //         autoBillObject.accountNumber = formData.autobillAccountNumber;
    //         autoBillObject.bsbNumber = formData.autobillBsbNumber;
    //         autoBillObject.accountType = formData.autobillAccountType;
    //     }

    //     return autoBillObject;
    // };

    const [validationErrors, setValidationErrors] = useState<
    WizardValidationError[]
    >([]);

    const { cart, initiateCart, updateCartQuantity } = useCart();
    const [boxShopDiscount, setBoxShopDiscount] = useState<number>(0);

    const [, updateFiles] = useImmer<Record<string, File | null>>({
        driversLicenseFile: null,
        proofOfAddressFile: null,
        proofOfInsuranceFile: null,
    });

    const updateFile = (key: string, file: File | null) => {
        updateFiles((draft) => {
            draft[key] = file;
            return draft;
        });
    };

    const updateDate = (key: string, date: Date) => {
        updateForm((draft) => {
            draft[key] = date.toISOString();
            return draft;
        });
    };

    const updateNeededDate = (date: Date) => {
        updateForm((draft) => {
            draft.neededDate = date.toISOString();
            return draft;
        });
    };

    const setTenantCommercial = (value: string) => {
        updateForm((draft) => {
            draft.tenant_commercial = value;
        });
        if (!cookies.selectedCustomerType) {
            setCookie('selectedCustomerType', value === 'true' ? 'business' : 'personal', {
                path: '/',
                maxAge: 60 * 60 * 24 * 7,
            });
        }
    };

    const update = (key: string, event: React.BaseSyntheticEvent) => {
        updateForm((draft) => {
            if (key === 'cardNumber') {
                const value = String(event.target.value).replace(/\s+/g, '');
                draft[key] = value;
            } else {
                draft[key] = String(event.target.value);

                // Sync to alternate address if checkbox is checked
                if (useSameAddress) {
                    if (key === 'address') draft.address_alternate = String(event.target.value);
                    if (key === 'city') draft.city_alternate = String(event.target.value);
                    if (key === 'state') draft.state_alternate = String(event.target.value);
                    if (key === 'postcode') draft.postcode_alternate = String(event.target.value);
                }
            }
            return draft;
        });
    };

    const handleUseSameAddressChange = (checked: boolean) => {
        setUseSameAddress(checked);
        if (checked) {
            updateForm((draft) => {
                draft.address_alternate = draft.address;
                draft.city_alternate = draft.city;
                draft.state_alternate = draft.state;
                draft.postcode_alternate = draft.postcode;
                return draft;
            });
        }
    };

    const updateExpiryDate = (
        value: Record<string, string | number>,
        autobill = false,
    ) => {
        const monthKey = autobill ? 'autobillCreditCardExpiryMonth' : 'cardExpiryMonth';
        const yearKey = autobill ? 'autobillCreditCardExpiryYear' : 'cardExpiryYear';
        if (!value.floatValue) {
            updateForm((draft) => {
                draft[monthKey] = '';
                draft[yearKey] = '';
                return draft;
            });
        } else {
            updateForm((draft) => {
                const [month = '', year = ''] = String(value.formattedValue).split('/');
                draft[monthKey] = month;
                draft[yearKey] = year;
                return draft;
            });
        }
    };

    // A saved quote can hold a date the site no longer takes, such as one before it opens.
    const bookableNeededDate = (date: Date): Date => {
        const { min } = getDatePickerRange(location, 'movein');
        return min && moment(date).isBefore(min, 'day') ? min : date;
    };

    const populateContactFormData = () => {
        if (res) {
            updateForm((draft) => {
                draft.firstName = res.firstName ?? '';
                draft.lastName = res.lastName ?? '';
                draft.email = res.email ?? '';
                draft.phone = res.phone ?? '';
                draft.address = res.addr1 ?? '';
                draft.city = res.city ?? '';
                if (RapidStorSettings(location).state().show()) {
                    draft.state = res.state ?? '';
                }
                if (RapidStorSettings(location).postalCode().show()) {
                    draft.postcode = res.postalCode ?? '';
                }

                // we have this check here to stop issues coming back to the move-in page after being on the move-in confirmed page once.
                // moveInContext doesn't get returned by the API call after the move-in has been finalised.
                if (res.moveInContext) {
                    // The calendar day entered in the contact details modal, not the fallback
                    // date the form was initialised with before the reservation loaded.
                    const restoredNeededDate = fromNeededDate(res.moveInContext.neededDate);
                    if (restoredNeededDate) {
                        draft.neededDate = bookableNeededDate(restoredNeededDate).toISOString();
                    }
                    draft.insuranceCoverage = res.moveInContext.movein?.insuranceCoverage ?? '';
                    if (RapidStorSettings(location).alternateContact().show()) {
                        const alternativeContact = res.moveInContext.tenant?.alternativeContact;
                        draft.firstName_alternate = alternativeContact?.firstName ?? '';
                        draft.lastName_alternate = alternativeContact?.lastName ?? '';
                        draft.email_alternate = alternativeContact?.email ?? '';
                        draft.phone_alternate = alternativeContact?.phone ?? '';
                        draft.address_alternate = alternativeContact?.address ?? '';
                        draft.city_alternate = alternativeContact?.city ?? '';
                        if (RapidStorSettings(location).state().show()) {
                            draft.state_alternate = alternativeContact?.state ?? '';
                        }
                        if (RapidStorSettings(location).postalCode().show()) {
                            draft.postcode_alternate = alternativeContact?.postalCode ?? '';
                        }
                    }
                }
                return draft;
            });
        }
    };

    const updateCartItem = (id: string, quantity: number) => {
        const result = updateCartQuantity(id, quantity);
        if (result) {
            setProductRemoval(result);
        }
    };

    const formErrors = useMemo(() => {
        const inputName: string[] = ['termsAndConditionsAgree'];

        return validationErrors?.filter((error) => inputName.includes(error.name));
    }, [validationErrors]);

    const showProductRemovalModal = useMemo(() => productRemoval !== null, [productRemoval]);

    useEffect(() => {
        if (
            moveInWizardDataInit?.reservation?.finalized
            && searchParams.get('error') !== 'true'
        ) {
            const completionRedirectUrl = location.rapidstor.jblRedirects.afterMoveIn.url;
            const useCompletionRedirect = location.rapidstor.jblRedirects.afterMoveIn.enabled;

            if (useCompletionRedirect && completionRedirectUrl) {
                window.location.replace(completionRedirectUrl);
            } else {
                navigate(`/move-in-confirmed/${res?.token}`);
            }
        } else if (
            moveInWizardDataInit?.reservation?.finalized
            && searchParams.get('error') === 'true'
        ) {
            navigate(
                `/move-in-confirmed/${res?.token}?error=true&message=${searchParams.get(
                    'message',
                )}`,
            );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moveInWizardDataInit]);

    useEffect(() => {
        const setup = async () => {
            setCostRetrieve(undefined);
            const result = await fetchCostRetrieve();
            setCostRetrieve(result);
            updateForm((draft) => {
                if (draft.lastName === '.') {
                    draft.lastName = '';
                }
                if (res?.dateOfBirth) {
                    draft.dateOfBirth = res.dateOfBirth;
                }
            });
        };

        if (res && res.token && !costRetrieve) {
            enqueueReservationTask(setup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [res]);

    const selectedInsuranceCoverage = useMemo(() => {
        if (form.insuranceCoverage !== '') {
            return availableInsuranceCoverage?.find(
                (coverage) => coverage._id === form.insuranceCoverage,
            );
        }

        if (reservation?.moveInContext?.movein?.insuranceCoverage) {
            return availableInsuranceCoverage?.find((coverage) => {
                if (
                    typeof reservation?.moveInContext?.movein?.insuranceCoverage === 'string'
                ) {
                    return (
                        coverage._id === reservation?.moveInContext?.movein?.insuranceCoverage
                    );
                }

                if (
                    typeof reservation?.moveInContext?.movein?.insuranceCoverage === 'number'
                ) {
                    return (
                        coverage.InsurCoverageID === reservation?.moveInContext?.movein?.insuranceCoverage
                    );
                }

                return false;
            });
        }

        return undefined;
    }, [
        availableInsuranceCoverage,
        form.insuranceCoverage,
        reservation?.moveInContext?.movein?.insuranceCoverage,
    ]);

    useEffect(() => {
        if (searchParams.get('error') === 'true') {
            const error = `An error occurred during the move in process\n CODE: ${searchParams.get(
                'code',
            )}\n MESSAGE: ${searchParams.get('message')}`;
            firebaseLogEvent('move_in_sca_error', {});
            Logger.error(`[SCAMoveIn] Error: ${error}`, location, [
                {
                    name: 'rapidstor_reservationToken',
                    value: `${res?.token}`,
                },
                {
                    name: 'rapidstor_corpToken',
                    value: `${window.rapidstor_settings.rapidstor_options.corpToken}`,
                },
                {
                    name: 'sca_error_code',
                    value: `${searchParams.get('code')}`,
                },
                {
                    name: 'sca_error_message',
                    value: `${searchParams.get('message')}`,
                },
            ]);
            const deconstructedError = deconstructScaErrorMessage(error);
            if (!deconstructedError) {
                if (window.rapidstor_settings.app_config.journey.dev) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                }
                setMoveInError(t('errors:10000'));
            } else {
                setMoveInError(t(`errors:${deconstructedError.reasonCode}`));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, res?.token, searchParams]);

    useEffect(() => {
        const setupCart = () => {
            const lineItems: LineItem[] = [];

            moveInWizardMerchandise?.products.forEach((product) => {
                if (product.visible && !product.deleted) {
                    lineItems.push({
                        _id: product._id,
                        name: product.name,
                        key: `${product._id}_product_idx`,
                        price: product.price.priceBeforeTax,
                        displayPrice: product.price.displayPrice,
                        tax: product.price.tax,
                        inStock: product.inStock,
                        inventory: product.inventory.dcInStock,
                        picture: product.picture ?? undefined,
                        quantity: product.defaultSelected ? 1 : 0,
                        featured: product.featured,
                        featuredOrder: product.featuredOrder ?? undefined,
                        defaultSelected: product.defaultSelected,
                        removedText: product.removedText,
                        removedImage: product.removedImage,
                    });
                }
            });

            lineItems.sort((a, b) => {
                if (a.featured && b.featured && a.featuredOrder && b.featuredOrder) {
                    return a.featuredOrder - b.featuredOrder;
                } if (a.featured) {
                    return -1;
                } if (b.featured) {
                    return 1;
                }
                return 0;
            });

            initiateCart(lineItems);
        };

        if (merchandiseSuccess) {
            setupCart();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchandiseSuccess, moveInWizardMerchandise]);

    /**
   * @TODO Migrate this into JourneyContext
   */
    const checkCookie = () => {
        if (reservation) {
            setCookie(
                'reservation',
                {
                    token: reservation.token,
                    id: reservation._id,
                },
                {
                    path: window.location.pathname,
                    maxAge: 60 * 60 * 24 * 7,
                },
            );
        }
    };

    /**
   * @TODO Shouldn't need to even have this.
   */
    const submitPayment = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    };

    /**
   * @TODO Create a useForm hook to handle all this form crap.
   */
    const validateForm = async (): Promise<WizardValidationError[]> => {
        setValidationErrors([]);
        const errors: WizardValidationError[] = [];
        const excludeKeys: string[] = [];

        if (window.rapidstor_settings.app_config.journey.optionalMoveInFields) {
            excludeKeys.push(
                ...window.rapidstor_settings.app_config.journey.optionalMoveInFields,
            );
        }

        if (window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified') {
            excludeKeys.push(
                'title',
                'address',
                'city',
                'state',
                'postcode',
                'title_alternate',
                'firstName_alternate',
                'lastName_alternate',
                'email_alternate',
                'phone_alternate',
                'address_alternate',
                'city_alternate',
                'state_alternate',
                'postcode_alternate',
                'driverLicenseNumber',
                'driverLicenseState',
                'dateOfBirth',
                'idDocumentNumber',
                'password',
                'confirmPassword',
                'company',
                'companyNumber',
                'tenant_commercial',
                'pickupLocation',
                'declaredValueOfGoods',
                'declaredValueOfGoodsAgree',
                'marketingAgree',
                'autobillType',
                'autobillAccountNumber',
                'autobillBsbNumber',
                'autobillAccountType',
                'autobillUsePaymentDetails',
                'boxShopCouponId',
                'insuranceType',
                'licencePlate',
            );
        }

        if (form.tenant_commercial === 'false') {
            excludeKeys.push(...['company', 'companyNumber']);
        }

        if (
            reservation?.unitType.excludeFromInsurance
            || availableInsuranceCoverage?.length === 0
            || location.rapidstor.wizards.moveinSingle.hideInsurance
        ) {
            excludeKeys.push(
                ...[
                    'insuranceCoverage',
                    'declaredValueOfGoodsAgree',
                    'declaredValueOfGoods',
                ],
            );
        }

        if (
            window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified'
            && !RapidStorSettings(location).insurance().required()
        ) {
            excludeKeys.push('insuranceCoverage');
        }

        errors.push(
            ...validateAutobill(
                location,
                form,
                window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified',
                t('wizard:autobillRequired'),
            ),
        );

        if (!RapidStorSettings(location).title().show()) {
            excludeKeys.push(...['title', 'title_alternate']);
        }

        const excludedInputKeys = excludedInputs(location);
        const formKeys = Object.keys(form).filter(
            (key) => !excludedInputKeys.includes(key) && !excludeKeys.includes(key),
        );

        for (const key of formKeys) {
            let normalisedKey = normaliseFieldName(key);

            const getCorpTranslationsResult = getCorpTranslations(
                location.corpCode as CorporationCode,
            );

            const isWizardTranslationsSet = getCorpTranslationsResult && 'wizard' in getCorpTranslationsResult;

            if (key === 'insuranceCoverage') {
                if (
                    isWizardTranslationsSet
                    && 'insuranceCoverage' in getCorpTranslationsResult.wizard
                ) {
                    normalisedKey = getCorpTranslationsResult.wizard.insuranceCoverage.title;
                }
            }

            if (!validateString(form[key])) {
                errors.push({
                    name: key,
                    message: `${normalisedKey} is required`,
                });
            }

            if (key === 'email' && !validateEmail(form[key])) {
                errors.push({
                    name: key,
                    message: 'Email is invalid',
                });
            }

            if (key === 'termsAndConditionsAgree' && form[key] === 'false') {
                errors.push({
                    name: key,
                    message: 'Terms and Conditions must be agreed to continue',
                });
            }

            // Not stuffing all this logic into one if statement.
            const isDeclaredValueOfGoodsAgreeValid = (): boolean => {
                if (
                    (RapidStorSettings(location).insurance().required()
                    || RapidStorSettings(location).insurance().manualEntry().required())
                    && form.declaredValueOfGoodsAgree === 'false'
                ) {
                    return false;
                }

                if (
                    RapidStorSettings(location).insurance().optional()
                    && form.insuranceCoverage
                    && form.declaredValueOfGoodsAgree === 'false'
                    && location.rapidstor.wizards.moveinSingle.insuranceRefusedText
                ) {
                    return false;
                }

                return true;
            };

            if (
                key === 'declaredValueOfGoodsAgree'
                && !isDeclaredValueOfGoodsAgreeValid()
            ) {
                errors.push({
                    name: key,
                    message: 'You must agree to the statement below to continue',
                });
            }

            if (
                key === 'declaredValueOfGoods'
                && RapidStorSettings(location).insurance().manualEntry().required()
                && (form.declaredValueOfGoods === '' || form.declaredValueOfGoods === '0')
            ) {
                errors.push({
                    name: key,
                    message: 'Declared Value of Goods must be larger than 0',
                });
            }
        }

        if (RapidStorSettings(location).passwordFields().show()) {
            if (form.password !== form.confirmPassword) {
                errors.push({
                    name: 'passwordMissmatch',
                    message: 'Passwords do not match',
                });
            }

            if (
                !validatePassword(form.password)
                || !validatePassword(form.confirmPassword)
            ) {
                errors.push({
                    name: 'passwordInvalid',
                    message: 'Password Invalid',
                });
            }
        }

        if (RapidStorSettings(location).paymentForm().show()) {
            const cc = creditCardType(form.cardNumber);

            if (!validateCreditCardNumber(form.cardNumber, location) || !cc) {
                errors.push({
                    name: 'cardNumber',
                    message: 'Invalid card number',
                });
            }

            const formattedExpiryMonth = moment(form.cardExpiryMonth, 'MM').format(
                'MM',
            );
            const formattedExpiryYear = moment(form.cardExpiryYear, 'YYYY').format(
                'YYYY',
            );
            const lastDayOfMonth = moment(
                `${formattedExpiryYear}-${formattedExpiryMonth}`,
            ).endOf('month');
            const today = moment();

            if (lastDayOfMonth < today) {
                errors.push({
                    name: 'cardExpiry',
                    message: 'Card has expired',
                });
            }

            if (cc && !validateCreditCardCSV(cc, form.cardCsv)) {
                errors.push({
                    name: 'cardCsv',
                    message: 'Invalid CSV',
                });
            }
        }

        setValidationErrors(errors);

        if (window.rapidstor_settings.rapidstor_options.dev) {
            // eslint-disable-next-line no-console
            console.error(errors);
        }

        return errors;
    };

    const confirmBookingButtonText = (confirmText?: string): string => getConfirmButtonText(t, i18n, 'wizard:confirm_booking', confirmText);

    const setAddressUsingLoqate = async (
        address: LoqateAddressFindResponseItem,
        alternate?: boolean,
    ) => {
        const loqate = new LoqateValidator(form, location);

        if (address.Type === 'Address') {
            const result = await loqate.retrieveAddress(address.Id);
            if (result) {
                updateForm((draft) => {
                    if (alternate) {
                        draft.address_alternate = result.Line1;
                        draft.city_alternate = result.City;
                        draft.state_alternate = result.Province;
                        draft.postcode_alternate = result.PostalCode;
                    } else {
                        draft.address = `${result.Line1
                        } ${
                            result.Line2
                        } ${
                            result.Line3
                        } ${
                            result.Line4}`;
                        draft.city = result.City;
                        draft.state = result.Province;
                        draft.postcode = result.PostalCode;
                    }
                    return draft;
                });
            }
        } else {
            const containerResult = await loqate.validateResidential(
                address.Text,
                'Address',
                address.Id,
                alternate,
            );

            if (containerResult) {
                setValidationErrors((prev) => prev.filter(
                    (e) => e.name !== (alternate ? 'address_alternate' : 'address'),
                ));
                if (alternate) {
                    setLoqateAddressResults({ main: [], alternate: containerResult });
                } else {
                    setLoqateAddressResults({ main: containerResult, alternate: [] });
                }
                setLoqateLoading(undefined);
            } else {
                updateForm((draft) => {
                    if (alternate) {
                        draft.postcode_alternate = address.Text;
                    } else {
                        draft.postcode = address.Text;
                    }
                });
            }
        }
    };

    const loqateValidation = async (
        fieldName: string,
        value?: string,
        alternate?: boolean,
    ) => {
        if (!window.rapidstor_settings.rapidstor_options.loqate) {
            return;
        }

        if (!value && fieldName !== 'bank') {
            setLoqateAddressResults({ main: [], alternate: [] });
            setLoqateLoading(undefined);
            return;
        }

        if (fieldName !== 'address') {
            setLoqateLoading(fieldName as LoqateValidationFields);
        }

        const loqate = new LoqateValidator(form, location);
        const addressFieldsToValidate = alternate
            ? [form.address_alternate, form.city_alternate]
            : [form.address, form.city];

        if (RapidStorSettings(location).state().show()) {
            addressFieldsToValidate.push(
                alternate ? form.state_alternate : form.state,
            );
        }

        if (RapidStorSettings(location).postalCode().show()) {
            addressFieldsToValidate.push(
                alternate ? form.postcode_alternate : form.postcode,
            );
        }

        const bankFieldsToValidate = [
            form.autobillBsbNumber,
            form.autobillAccountNumber,
        ];

        if (fieldName === 'bank') {
            for (const field of bankFieldsToValidate) {
                if (!validateString(field)) {
                    setLoqateLoading(undefined);
                    return;
                }
            }
        }

        switch (fieldName) {
            case 'email':
                try {
                    await loqate.validateEmail(form.email);
                    setLoqateLoading(undefined);
                } catch (error) {
                    setLoqateLoading(undefined);
                }
                break;
            case 'address':
                try {
                    const results = await loqate.validateResidential(
                        addressFieldsToValidate.join(', '),
                        'Address',
                        undefined,
                        alternate,
                    );

                    if (results !== false) {
                        setLoqateAddressResults(
                            alternate
                                ? { main: [], alternate: results }
                                : { main: results, alternate: [] },
                        );
                    } else {
                        setLoqateAddressResults({ main: [], alternate: [] });
                    }

                    setLoqateLoading(undefined);
                } catch (error) {
                    Logger.warn(
                        `[loqateValidation] Failed validating address: ${error}`,
                        location,
                        [
                            {
                                name: 'address_entry',
                                value: addressFieldsToValidate.join(', '),
                            },
                        ],
                    );
                    setLoqateLoading(undefined);
                }
                break;
            case 'state':
            case 'postcode':
                try {
                    const results = await loqate.validateResidential(
                        addressFieldsToValidate.join(', '),
                        'Postcode',
                        undefined,
                        alternate,
                    );

                    if (results !== false) {
                        setLoqateAddressResults(
                            alternate
                                ? { main: [], alternate: results }
                                : { main: results, alternate: [] },
                        );
                    } else {
                        setLoqateAddressResults({ main: [], alternate: [] });
                    }

                    setLoqateLoading(undefined);
                } catch (error) {
                    Logger.warn(
                        `[loqateValidation] Failed validating postcode: ${error}`,
                        location,
                        [
                            {
                                name: 'address_entry',
                                value: addressFieldsToValidate.join(', '),
                            },
                        ],
                    );
                    setLoqateLoading(undefined);
                }
                break;
            case 'bank':
                try {
                    const result = await loqate.validateBank(
                        form.autobillBsbNumber,
                        form.autobillAccountNumber,
                    );
                    if (result) {
                        setBankName(result.Bank);
                    } else {
                        setBankName('');
                    }
                    setLoqateLoading(undefined);
                } catch (error) {
                    setBankName('');
                    setLoqateLoading(undefined);
                }
                break;
            default:
                break;
        }

        if (loqate.errors.length) {
            for (const error of loqate.errors) {
                if (!validationErrors.find((e) => e.name === error.name)) {
                    setValidationErrors((prev) => [...prev, error]);
                }
            }
        } else {
            setValidationErrors((prev) => prev.filter((e) => e.name !== fieldName));
        }
    };

    const runUpdateReservation = async (
        doCostRetrieve: boolean,
        insuranceCoverage?: string,
        neededDate?: Date,
    ) => {
        if (!reservation?.token) {
            Logger.info('No reservation token found', location);
            return undefined;
        }

        if (doCostRetrieve) {
            setCostRetrieve(undefined);
        }

        /**
     * @TODO Create a method in useCart for getting the cart itemns to add to the reservation
     */
        const cartItems: Record<string, number> = {};
        cart.forEach((item) => {
            if (item.quantity > 0) {
                cartItems[item._id] = item.quantity;
            }
        });

        try {
            const payload = reservationSvc.buildPreMoveInPatchPayload(
                reservation,
                {
                    ...form,
                    ...(neededDate ? { neededDate: neededDate.toISOString() } : {}),
                    insuranceCoverage: insuranceCoverage ?? form.insuranceCoverage,
                },
                cartItems,
                {},
                location,
            );
            const patchResult = await patchReservation(payload).unwrap();

            setReservation(patchResult);

            if (doCostRetrieve) {
                /**
         * @TODO Can't remember why this needs to get set undefined first. Try removing it.
         */
                setCostRetrieve(undefined);
                const result = await fetchCostRetrieve();
                setCostRetrieve(result);
            }

            return patchResult;
        } catch (error) {
            // The costs were cleared above; get them again so the pay button is not left disabled.
            if (doCostRetrieve) {
                setCostRetrieve(await fetchCostRetrieve());
            }

            if (error instanceof Error) {
                if (error.message === 'Invalid credit card number') {
                    const errors = validationErrors;
                    errors.push({
                        name: 'cardNumber',
                        message: 'Invalid credit card number',
                    });
                    setValidationErrors(errors);
                }
            } else {
                Logger.error(
                    `Failed updating reservation: ${JSON.stringify(error)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ],
                );
            }

            return undefined;
        }
    };

    const updateReservation = (
        doCostRetrieve: boolean,
        insuranceCoverage?: string,
        neededDate?: Date,
    ) => enqueueReservationTask(() => runUpdateReservation(doCostRetrieve, insuranceCoverage, neededDate));
    // The SCA charge is priced from the saved reservation, so wait until it is up to date.
    const pricingPending = !costRetrieve || insurancePending;

    const processBooking = async (payment_method?: PaymentProcessor) => {
        if (pricingPending) {
            return;
        }
        firebaseLogEvent('move_in_button_clicked', {});
        setMoveInError(undefined);
        setMoveInLoading(true);
        const errors = await validateForm();

        if (errors.length > 0) {
            firebaseLogEvent('move_in_validation_error', {});
            setMoveInError('Please fill in all required fields');
            setMoveInLoading(false);
            return;
        }

        const availableResult = await enqueueReservationTask(handleUnitAvailability);

        if (!availableResult) {
            return;
        }

        const updatedReservation = await updateReservation(false);
        if (!updatedReservation || !res?._id) {
            setMoveInError('Failed updating reservation');
            setMoveInLoading(false);
            return;
        }

        if (
            location.rapidstor.paymentGateways.use
            && location.rapidstor.paymentGateways.gateways.r6Pay
            && updatedReservation
        ) {
            const returnUrl = paymentGatewayReturnUrl(payment_method ?? PaymentProcessor.FINVERSE, updatedReservation?.token ?? '', 'move-in');

            const isStripeDirect = payment_method === PaymentProcessor.STRIPE_DIRECT;

            const payload = r6PayService.service.buildCreatePaymentLinkPayload({
                reservation: updatedReservation,
                location,
                currency: location.rapidstor.paymentGateways.gateways.r6Pay.currency,
                returnUrl,
                paymentMethod: payment_method ?? PaymentProcessor.FINVERSE,
                // Stripe Direct sends the contact details stored on the reservation.
                form: isStripeDirect ? undefined : form,
            });

            try {
                const result = await r6PayService.service.createPaymentLink(payload);
                if (!result) {
                    Logger.error('[processBooking] Failed R6Pay request', location, [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ]);
                    setMoveInError('R6Pay Create Payment Error');
                    setMoveInLoading(false);
                    return;
                }
                if (result.next_action.type === NextAction.REDIRECT_TO_URL) {
                    window.location.href = result.next_action.url;
                    return;
                } if (result.next_action?.type === NextAction.POST_TO_URL) {
                    r6PayService.service.postToUrl(
                        result.next_action.url,
                        result.next_action.form_data || {},
                    );
                    return;
                } if (result.next_action?.type === NextAction.USE_STRIPE_SDK) {
                    setStripeIntent({
                        clientSecret: result.next_action.client_secret,
                        publishableKey: result.next_action.publishable_key,
                        paymentLinkId: result.payment_link_id,
                        returnUrl,
                    });
                    setMoveInLoading(false);
                    return;
                }
            } catch (error) {
                Logger.error(
                    `[processBooking] Failed R6Pay request: ${JSON.stringify(error)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ],
                );
                setMoveInError('R6Pay Create Payment Error');
                setMoveInLoading(false);
                return;
            }
        }

        if (location.scaConfiguration && Object.keys(location.scaConfiguration).length > 0) {
            // Every SCA path below sends card details, and creditCardType returns undefined for a
            // number matching no known scheme. Resolve it once up front and treat an unrecognised
            // card as a validation failure, rather than sending undefined to the API.
            const cardTypeId = creditCardType(form.cardNumber);

            if (!cardTypeId) {
                firebaseLogEvent('move_in_validation_error', {});
                setMoveInError('Unsupported or invalid card number');
                setMoveInLoading(false);
                return;
            }

            try {
                // If autobilling is for cc do billing-info-update
                let autobillResult;
                const {
                    host,
                    href,
                    pathname,
                    protocol,
                    search,
                } = window.location;

                if (form.autobillType === 'cc' && !corporationData?.customFeatures?.skipScaAutobill?.enabled) {
                    autobillResult = await makeScaBillingInfoUpdate({
                        locationCode: location.locationCode,
                        corpCode: location.corpCode,
                        payload: {
                            cardHoldersName: form.cardHolderName,
                            cardNumber: form.cardNumber,
                            cardExpiryMonth: form.cardExpiryMonth,
                            cardExpiryYear: form.cardExpiryYear,
                            cardCVV: form.cardCsv,
                            cardTypeId,
                            view: ScaApplicationViews.standalone,
                            reservationId: res?._id,
                            standaloneReturnUrl: `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/autobill`,
                            frontendUrls: {
                                original: href,
                                success: `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/autobill`,
                                fail: `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/autobill`,
                            },
                        },
                    }).unwrap();
                }

                // If the cart has boxshop items, create a separate boxshop payment.
                // It chains between the move-in payment and the autobill step.
                let boxshopPaymentResult;
                const hasBoxShopItems = cart.some((item) => item.quantity > 0);
                if (hasBoxShopItems) {
                    boxshopPaymentResult = await makeScaMoveInPayment({
                        locationCode: location.locationCode,
                        corpCode: location.corpCode,
                        payload: {
                            cardHoldersName: form.cardHolderName,
                            cardNumber: form.cardNumber,
                            cardExpiryMonth: form.cardExpiryMonth,
                            cardExpiryYear: form.cardExpiryYear,
                            cardCVV: form.cardCsv,
                            cardTypeId,
                            reservationId: res?._id,
                            view: ScaApplicationViews.standalone,
                            parentUri: href,
                            standaloneReturnUrl: autobillResult?.redirect ??
                                `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/payment`,
                            frontendUrls: {
                                original: href,
                                success: autobillResult?.redirect ??
                                    `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/payment`,
                                fail: `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/payment`,
                            },
                        },
                    }).unwrap();
                }

                const result = await makeScaMoveInPrepare({
                    locationCode: location.locationCode,
                    corpCode: location.corpCode,
                    payload: {
                        cardHoldersName: form.cardHolderName,
                        cardNumber: form.cardNumber,
                        cardExpiryMonth: form.cardExpiryMonth,
                        cardExpiryYear: form.cardExpiryYear,
                        cardCVV: form.cardCsv,
                        cardTypeId,
                        reservationId: res?._id,
                        view: ScaApplicationViews.standalone,
                        parentUri: href,
                        standaloneReturnUrl: boxshopPaymentResult?.redirect ??
                            autobillResult?.redirect ??
                            `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/move-in`,
                        frontendUrls: {
                            original: href,
                            success: boxshopPaymentResult?.redirect ??
                                autobillResult?.redirect ??
                                `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/move-in`,
                            fail: `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/move-in`,
                        },
                    },
                }).unwrap();

                window.location.href = result.redirect;
            } catch (error) {
                firebaseLogEvent('move_in_sca_error', {});
                Logger.error(
                    `[processBooking] Failed SCA move in request: ${JSON.stringify(
                        error,
                    )}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ],
                );
                setMoveInError(`${JSON.stringify(error)}`);
                setMoveInLoading(false);
            }
        } else {
            try {
                // Join the move-in socket room before submitting so the completion event
                // is captured (the server also replays it on join).
                if (res?._id) {
                    startMoveInSocketListening(res._id);
                }
                const result = await makeMoveIn({
                    locationCode: location.locationCode,
                    corpCode: location.corpCode,
                    payload: {
                        ...form,
                        autobill: reservationSvc.buildAutobillObject(form),
                        card: {
                            name: form.cardHolderName,
                            type: creditCardType(form.cardNumber),
                            number: form.cardNumber,
                            cvv: form.cardCsv,
                            expiryMonth: moment(form.cardExpiryMonth, 'MM').format('MM'),
                            expiryYear: moment(form.cardExpiryYear, 'YYYY').format('YYYY'),
                        },
                    },
                    token: res?.token,
                }).unwrap();

                if (result.operation_result === 'success') {
                    setMoveInError('');
                    navigate(`/api/payment-redirect/${res?.token}/move-in`);
                } else {
                    firebaseLogEvent('move_in_error', {});
                    Logger.error(
                        `[processBooking] Failed move in request: ${JSON.stringify(
                            result,
                        )}`,
                        location,
                    );
                    setMoveInError(`${JSON.stringify(result.operation_message)}`);
                    setMoveInLoading(false);
                    // Refetch wizard init — if the reservation was actually finalized
                    // (e.g. after-move-in soft error), the finalized useEffect will
                    // navigate to the confirmation page automatically.
                    refetchMoveInWizardDataInit();
                }
            } catch (error) {
                Logger.error(
                    `[processBooking] Failed move in request: ${JSON.stringify(error)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ],
                );
                firebaseLogEvent('move_in_error', {});

                if (error instanceof Error) {
                    setMoveInError(error.message);
                } else if (typeof error === 'string') {
                    setMoveInError(error);
                } else {
                    setMoveInError(`${JSON.stringify(error)}`);
                }
                setMoveInLoading(false);
            }
        }
    };

    /**
   * @TODO this can go into the useForm hook or whatever gets created to manage that state
   */
    const fillForm = async () => {
        updateForm((draft) => {
            draft.title = 'Mr';
            draft.firstName = 'Test';
            draft.lastName = 'User';
            draft.email = 'test@test.com';
            draft.phone = '123123123123';
            draft.address = '123 Test St';
            draft.city = 'Test City';
            draft.state = 'Test State';
            draft.postcode = '1234';

            if (RapidStorSettings(location).alternateContact().show()) {
                draft.title_alternate = 'Mrs';
                draft.firstName_alternate = 'Alternate';
                draft.lastName_alternate = 'User';
                draft.email_alternate = 'test@test.com';
                draft.phone_alternate = '123123123123';
                draft.address_alternate = '123 Test St';
                draft.city_alternate = 'Test City';
                draft.state_alternate = 'Test State';
                draft.postcode_alternate = '1234';
            }

            draft.password = 'ValidPassword1';
            draft.confirmPassword = 'ValidPassword1';

            if (RapidStorSettings(location).driversLicense().show()) {
                draft.driverLicenseNumber = '123456789';
                draft.dateOfBirth = moment().format('YYYY-MM-DD');
                draft.idDocumentNumber = '123456789';

                if (!location.rapidstor.wizards.moveinSingle.hideDriversLicenceState) {
                    draft.driverLicenseState = 'VIC';
                }
            }

            if (RapidStorSettings(location).insurance().manualEntry().required()) {
                draft.declaredValueOfGoodsAgree = 'true';
                draft.declaredValueOfGoods = '1000';
            }

            if (RapidStorSettings(location).dateOfBirth().show()) {
                draft.dateOfBirth = moment().subtract(18, 'years').format('YYYY-MM-DD');
            }

            draft.cardHolderName = 'test user';
            draft.cardNumber = '4242424242424242';
            draft.cardExpiryMonth = '12';
            draft.cardExpiryYear = `${new Date().getFullYear() + 1}`.slice(-2);
            draft.cardCsv = '123';
        });
    };

    const getInitialSelectedInsuranceCoverage = (): string => {
        if (
            res?.unitType.descriptor.defaultInsuranceCoverage
            && res?.moveInContext?.movein?.insuranceCoverage === null
            && moveInWizardDataInit?.siteInsuranceCoverage.some(
                (coverage) => coverage._id === res?.unitType.descriptor.defaultInsuranceCoverage,
            )
        ) {
            return res?.unitType.descriptor.defaultInsuranceCoverage;
        }

        if (
            res?.moveInContext?.movein?.insuranceCoverage
            && res?.moveInContext?.movein?.insuranceCoverage !== ''
        ) {
            const found = availableInsuranceCoverage?.find((coverage) => {
                if (typeof res?.moveInContext?.movein?.insuranceCoverage === 'string') {
                    return coverage._id === res?.moveInContext?.movein?.insuranceCoverage;
                } if (
                    typeof res?.moveInContext?.movein?.insuranceCoverage === 'number'
                ) {
                    return (
                        coverage.InsurCoverageID
            === res?.moveInContext?.movein?.insuranceCoverage
                    );
                }
                return false;
            });
            if (found) {
                return found._id.toString();
            }
        }

        return '';
    };

    const initiateR6Pay = async () => {
        await r6PayService.service?.getPaymentMethods();
    };

    useEffect(() => {
        if (res) {
            setReservation(res);
            populateContactFormData();

            const { oAuthReturn } = props;

            if (oAuthReturn) {
                const oAuthEmail = searchParams.get('username');
                const oAuthTenantId = searchParams.get('tenantId');
                const oAuthFirstName = searchParams.get('firstName');
                const oAuthLastName = searchParams.get('lastName');

                updateForm((draft) => {
                    if (oAuthFirstName) {
                        draft.firstName = oAuthFirstName;
                    }
                    if (oAuthLastName) {
                        draft.lastName = oAuthLastName;
                    }
                    if (oAuthEmail) {
                        draft.email = oAuthEmail;
                    }
                });

                const parsedTenantId = Number(oAuthTenantId);
                if (oAuthTenantId && Number.isFinite(parsedTenantId) && res.tenantId !== parsedTenantId) {
                    Logger.info(`[MoveIn] OAuth returned different tenantId (${oAuthTenantId}) than current reservation (${res.tenantId})`, location);

                    const updatedReservation = {
                        ...res,
                        tenantId: parsedTenantId,
                        email: oAuthEmail || res.email,
                        firstName: oAuthFirstName || res.firstName,
                        lastName: oAuthLastName || res.lastName,
                        moveInContext: {
                            ...res.moveInContext,
                            tenant: {
                                ...res.moveInContext.tenant,
                                tenantId: parsedTenantId,
                                firstName: oAuthFirstName || res.moveInContext.tenant?.firstName,
                                lastName: oAuthLastName || res.moveInContext.tenant?.lastName,
                                email: oAuthEmail || res.moveInContext.tenant?.email,
                            },
                        },
                    };
                    setReservation(updatedReservation);
                }
            }

            if (moveInInitSuccess) {
                updateForm((draft) => {
                    draft.insuranceCoverage = getInitialSelectedInsuranceCoverage();
                    if (
                        draft.insuranceCoverage
                        && RapidStorSettings(location).insurance().manualEntry().show()
                    ) {
                        const selectedInsuranceCoverageData = availableInsuranceCoverage?.find(
                            (coverage) => coverage._id === draft.insuranceCoverage,
                        );
                        draft.declaredValueOfGoods = selectedInsuranceCoverageData
                            ? `${selectedInsuranceCoverageData.dcCoverage}`
                            : '';
                    }
                    return draft;
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSuccess, res, moveInInitSuccess]);

    // The costs and the move-in use the saved date, so save a date moved forward on load.
    const neededDateCorrected = useRef(false);
    useEffect(() => {
        const savedNeededDate = fromNeededDate(reservation?.moveInContext?.neededDate);
        if (!savedNeededDate || neededDateCorrected.current) {
            return;
        }
        const neededDate = bookableNeededDate(savedNeededDate);
        if (neededDate !== savedNeededDate) {
            neededDateCorrected.current = true;
            updateReservation(true, undefined, neededDate);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservation]);

    useEffect(() => {
        if (
            !reservationIdentifier
            || !window.rapidstor_settings.rapidstor_options.corpToken
        ) {
            navigate('/');
        } else {
            setReservation(res);
            checkCookie();

            if (
                location.rapidstor.paymentGateways.use
        && location.rapidstor.paymentGateways.gateways.r6Pay
        && !r6PayService.service
            ) {
                initiateR6Pay();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A Stripe intent is created for a fixed amount, so drop it only when something that
    // affects the price changes underneath it. Contact fields (name/email) must NOT be in
    // this list: Apple Pay's own autofill can populate them after the Payment Element is
    // already showing, and including them here wiped out the payment UI as soon as that fired.
    useEffect(() => {
        if (stripeIntent) {
            setStripeIntent(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.neededDate, form.insuranceCoverage]);

    useEffect(() => {
        if (reservation && unitList) {
            Logger.info('[MoveIn] Got reservation and unitList data', location);

            const [foundDescriptor, foundUnitType] = unitList.descriptors.reduce<
            [Descriptor | null, UnitType | null]
            >(
                (acc, d) => {
                    const found = d.unitTypes.find((uT: UnitType) => uT._id === reservation.unitType._id);
                    if (found) {
                        return [d, found];
                    }
                    return acc;
                },
                [null, null],
            );

            if (foundDescriptor && foundUnitType) {
                Logger.info('[MoveIn] Found matching unitType', location, []);

                if (reservation.persona) {
                    const unitTypePersona: UnitType = {
                        ...foundUnitType,
                        rates: {
                            ...foundUnitType.rates,
                            rate: reservation.rates.rate,
                            discounted: reservation.rates.discounted,
                            discountedRate: reservation.rates.discountedRate ?? 0,
                        },
                    };

                    const descriptorPersona: Descriptor = {
                        ...foundDescriptor,
                        name: reservation.unitType.descriptor.name,
                        tags: reservation.unitType.descriptor.tags,
                        description: reservation.unitType.descriptor.description,
                        descriptions: reservation.unitType.descriptor.descriptions,
                        specialText: reservation.unitType.descriptor.specialText,
                    };
                    setUnitType(unitTypePersona);
                    setDescriptor(descriptorPersona);
                    if (reservation.persona._id) {
                        store.dispatch(setPersonaId(reservation.persona?._id));
                    }

                    setupSliceData(
                        {
                            unitType: unitTypePersona,
                            descriptor: descriptorPersona,
                            dealObject: reservation.deal ?? undefined,
                            deal: reservation.deal?._id,
                            token: reservation.token,
                        },
                        {
                            location,
                            unitType: unitTypePersona,
                            descriptor: descriptorPersona,
                            descriptors: unitList.descriptors,
                        },
                    );
                } else {
                    setUnitType(foundUnitType);
                    setDescriptor(foundDescriptor);

                    setupSliceData(
                        {
                            unitType: foundUnitType,
                            descriptor: foundDescriptor,
                            dealObject: reservation.deal ?? undefined,
                            deal: reservation.deal?._id,
                            token: reservation.token,
                        },
                        {
                            location,
                            unitType: foundUnitType,
                            descriptor: foundDescriptor,
                            descriptors: unitList.descriptors,
                        },
                    );
                }
                checkCookie();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitList, reservation]);

    // The wizard cannot be trusted without its init data, which carries the live
    // SiteLink state of the reservation. Failing loudly beats rendering a payment form.
    if (moveInInitError) {
        return (
            <ErrorComponent
                customError="Failed to load the move in wizard initialisation data"
                location={location}
            />
        );
    }

    if (blockingStatus) {
        return (
            <ReservationUnavailable
                status={blockingStatus}
                onGoBack={() => navigate('/')}
            />
        );
    }

    /** @todo Temporary fix, will need to improve this error handling process once the stack trace errors are fixed on rapidstor api */
    if (moveInCostRetrieveError) {
        return (
            <ErrorComponent
                customError={moveInCostRetrieveError as string}
                location={location}
            />
        );
    }

    function getReturnUrl(): string | undefined {
        const baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname.split('?')[0]}${window.location.hash.split('?')[0]}`;
        if (baseUrl.includes('oauth-return')) {
            return baseUrl;
        }

        return `${baseUrl}/oauth-return`;
    }

    return (
        <div
            className={`${classes.container} ${
                moveInLoading && classes.disabledContainer
            }`}
        >
            <div className={`${classes.header}`}>
                <div>
                    <Button
                        onclick={() => {
                            firebaseLogEvent('move_in_back_button_clicked', {});
                            setTimeout(() => {
                                if (
                                    window.rapidstor_settings.app_config.journey.variant === JourneyVariant.STANDARD_UNIT_LISTING
                                ) {
                                    navigate('/');
                                    return;
                                }
                                fadeOut();
                                const backUrl = window.rapidstor_settings.app_config.pages.bookUnit === 'map'
                                    ? '/book-unit'
                                    : '/customise-booking';
                                navigate(backUrl);
                            }, 300);
                        }}
                        type="primaryOnWhite"
                        size="medium"
                        chevron="left"
                    >
                        <span>{t('wizard:back')}</span>
                    </Button>
                </div>
                <span className={`${classes.title}`}>{t('wizard:scheduleMoveIn')}</span>
                <div />
            </div>

            {moveInInitLoading || !moveInInitSuccess ? (
                <Spinner size="100" />
            ) : (
                <>
                    <div />
                    {window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified' ? (
                        <SimplifiedCheckout
                            descriptor={descriptor}
                            unitType={unitType}
                            deal={reservation?.deal ?? undefined}
                            costRetrieve={costRetrieve}
                            cart={cart}
                            merchandiseDiscount={boxShopDiscount}
                            form={form}
                            update={update}
                            updateForm={updateForm}
                            updateNeededDate={(date: Date) => {
                                updateNeededDate(date);
                                updateReservation(true, undefined, date);
                            }}
                            updateExpiryDate={updateExpiryDate}
                            updateReservation={updateReservation}
                            validationErrors={validationErrors}
                            availableInsuranceCoverage={availableInsuranceCoverage}
                            reservation={reservation}
                            moveInLoading={moveInLoading}
                            pricingPending={pricingPending}
                            moveInError={moveInError}
                            setMoveInError={setMoveInError}
                            processBooking={processBooking}
                            r6PayService={r6PayService}
                            confirmBookingButtonText={confirmBookingButtonText}
                            stripeIntent={stripeIntent}
                        />
                    ) : (
                        <>
                            <WizardDescriptor
                                descriptor={descriptor}
                                unitType={unitType}
                                deal={reservation?.deal ?? undefined}
                            />

                            <div className={`${classes.fold}`} />

                            {moveInError && <Alert type="error" text={moveInError} />}
                            {location.administrative.useMtasForAuth &&
                        location.administrative.useMtasForAuth === true && (
                        <OAuthIntegrations
                            continueTo={getReturnUrl()}
                            metadata={{
                                tenantId: reservation?.tenantId || '',
                                reservationToken: res?.token || '',
                                view: 'move-in',
                            }}
                            onIntegrationError={(integration, error) => {
                                Logger.error(
                                    `[OAuthIntegrations] integration error: ${error}`,
                                    location,
                                    [
                                        {
                                            name: 'rapidstor_reservationToken',
                                            value: `${res?.token}`,
                                        },
                                    ],
                                );
                                setMoveInError(`${error}`);
                            }}
                            style={{ marginBottom: '1.5rem' }}
                        />
                    )}

                            <ContactDetailsForm
                                form={form}
                                update={update}
                                updateForm={updateForm}
                                updateDate={updateDate}
                                updateNeededDate={(date: Date) => {
                            updateNeededDate(date);
                            updateReservation(true, undefined, date);
                        }}
                                setTenantCommercial={setTenantCommercial}
                                validationErrors={validationErrors}
                                loqateValidation={loqateValidation}
                                loqateLoading={loqateLoading}
                                loqateAddressResults={loqateAddressResults}
                                setLoqateAddressResults={setLoqateAddressResults}
                                setAddressUsingLoqate={setAddressUsingLoqate}
                            />

                            {RapidStorSettings(location).alternateContact().show() && (
                            <ContactDetailsForm
                                form={form}
                                update={update}
                                updateDate={updateDate}
                                updateForm={updateForm}
                                updateNeededDate={(date: Date) => {
                                updateNeededDate(date);
                                updateReservation(true, undefined, date);
                            }}
                                alternate
                                validationErrors={validationErrors}
                                loqateValidation={loqateValidation}
                                loqateLoading={loqateLoading}
                                setAddressUsingLoqate={setAddressUsingLoqate}
                                loqateAddressResults={loqateAddressResults}
                                setLoqateAddressResults={setLoqateAddressResults}
                                useSameAddress={useSameAddress}
                                onUseSameAddressChange={handleUseSameAddressChange}
                            />
                    )}

                            <div className={`${classes.fold}`} />

                            {RapidStorSettings(location).passwordFields().show() && (
                            <>
                                <AccountDetails
                                    password={form.password}
                                    confirmPassword={form.confirmPassword}
                                    updatePassword={updateForm}
                                    validationErrors={validationErrors}
                                />

                                <div className={`${classes.fold}`} />
                            </>
                    )}

                            {RapidStorSettings(location).identityVerification().show() && (
                            <>
                                <IdentityVerification
                                    update={update}
                                    updateFile={updateFile}
                                    form={form}
                                    validationErrors={validationErrors}
                                />
                                <div className={`${classes.fold}`} />
                            </>
                    )}

                            {RapidStorSettings(location)
                        .insurance()
                        .show(reservation, availableInsuranceCoverage) && (
                        <>
                            <InsuranceCoverageComponent
                                validationErrors={validationErrors}
                                update={updateForm}
                                coverages={availableInsuranceCoverage}
                                currentlySelected={
                                    availableInsuranceCoverage?.findIndex(
                                        (i) => i._id === selectedInsuranceCoverage?._id,
                                    ) ?? -1
                                }
                                form={form}
                                updateReservation={updateReservation}
                                onPendingChange={setInsurancePending}
                                taxRate={
                                    moveInWizardDataInit.insuranceCharge.dcTax1Rate
                                    + moveInWizardDataInit.insuranceCharge.dcTax2Rate
                                }
                            />
                            <div className={`${classes.fold}`} />
                        </>
                    )}

                            {RapidStorSettings(location)
                        .proofOfInsurance()
                        .show(form.insuranceType) && (
                        <>
                            <ProofOfInsurance
                                updateFile={updateFile}
                                resTenant={reservation?.tenantId}
                                resToken={reservation?.token}
                                validationErrors={validationErrors}
                            />

                            <div className={`${classes.fold}`} />
                        </>
                    )}

                            {RapidStorSettings(location).proofOfAddress().show() && (
                            <>
                                <ProofOfAddress
                                    updateFile={updateFile}
                                    resTenant={reservation?.tenantId}
                                    resToken={reservation?.token}
                                />

                                <div className={`${classes.fold}`} />
                            </>
                    )}

                            {RapidStorSettings(location).boxshop().show() && (
                            <>
                                <Merchandise
                                    cart={cart}
                                    pickupLocation={form.pickupLocation}
                                    updateCartItem={updateCartItem}
                                    updatePickupLocation={(locationString: string) => {
                                    updateForm((draft) => {
                                        draft.pickupLocation = locationString;
                                        return draft;
                                    });
                                }}
                                    setBoxShopDiscount={(
                                    discount: RapidStorBoxShopCouponResponse,
                                ) => {
                                    setBoxShopDiscount(discount.discountPercentage);
                                    updateForm((draft) => {
                                        draft.boxShopCouponId = discount._id;
                                    });
                                }}
                                    boxShopDiscount={boxShopDiscount}
                                />
                                <div className={`${classes.fold}`} />
                            </>
                            )}

                            <WizardPricingTable
                                costRetrieve={costRetrieve}
                                charges={undefined}
                                cart={cart}
                                moveIn
                                merchandiseDiscount={boxShopDiscount}
                                style={
                            window.rapidstor_settings.rapidstor_options.pricingTable?.style
                        }
                                showTaxRow={
                            window.rapidstor_settings.rapidstor_options.pricingTable
                                ?.showTaxRow
                        }
                            />

                            <div className={`${classes.fold}`} />

                            {RapidStorSettings(location).paymentForm().show() && (
                            <>
                                <PaymentForm
                                    onSubmit={submitPayment}
                                    form={form}
                                    submittingPayment={false}
                                    update={update}
                                    updateExpiryDate={updateExpiryDate}
                                    validationErrors={validationErrors}
                                    moveIn
                                />
                                <div className={`${classes.fold}`} />
                            </>
                            )}

                            {RapidStorSettings(location).autobill().show() && (
                            <>
                                <AutoBill
                                    form={form}
                                    update={updateForm}
                                    loqateValidation={loqateValidation}
                                    updateExpiryDate={updateExpiryDate}
                                    loqateLoading={loqateLoading}
                                    validationErrors={validationErrors}
                                    bankName={bankName}
                                />
                                <div className={`${classes.fold}`} />
                            </>
                            )}

                            <span className={`${classes.header_text}`}>
                                {t('wizard:termsAndConditions')}
                            </span>

                            {moveInWizardDataInit?.siteTermsAndConditions && (
                            <div className={`${classes.terms_and_conditions}`}>
                                <p
                                    dangerouslySetInnerHTML={{
                                    __html: moveInWizardDataInit?.siteTermsAndConditions,
                                }}
                                />
                            </div>
                            )}

                            {RapidStorSettings(location).dangerousGoodsWarning().show() && (
                                <div className={`${classes.dangerous_goods_warning}`}>
                                    <img src={RapidStorSettings(location).dangerousGoodsWarning().value()} alt="Dangerous Goods Warning" />
                                </div>
                            )}

                            {formErrors
                        .filter((error) => error.name === 'termsAndConditionsAgree')
                        .map((error, index) => (
                            <ErrorTag
                                key={`${index}_error_idx`}
                                error={{
                                    name: error.name,
                                    message: error.message,
                                }}
                            />
                        ))}

                            <div className={`${classes.opt_in_container}`}>
                                <CircleCheckbox
                                    value={form.termsAndConditionsAgree}
                                    update={updateForm}
                                    label={t('wizard:termsAndConditionsAgree')}
                                    formKey="termsAndConditionsAgree"
                                />
                                <CircleCheckbox
                                    value={form.marketingAgree}
                                    update={updateForm}
                                    label={t('wizard:marketingAgree')}
                                    formKey="marketingAgree"
                                />
                                {RapidStorSettings(location).assistanceMoving().show() && (
                                    <CircleCheckbox
                                        value={form.assistanceMovingRequested}
                                        update={updateForm}
                                        label={t('wizard:assistanceMovingAgree')}
                                        formKey="assistanceMovingRequested"
                                    />
                                )}
                            </div>

                            {moveInError && <Alert type="error" text={moveInError} />}

                            {stripeIntent && (
                                <StripePaymentForm
                                    intent={stripeIntent}
                                    onError={(msg) => setMoveInError(msg)}
                                    buttonText="Pay Now"
                                />
                            )}

                            <div className={`${classes.confirm_booking}`}>
                                {(() => {
                                    if (stripeIntent) {
                                        return null;
                                    }
                                    if (!RapidStorSettings(location).paymentForm().show()) {
                                        return r6PayService.service?.paymentMethods?.map((method) => (
                                            <Button
                                                key={method.id}
                                                disableScroll
                                                onclick={() => {
                                                    processBooking(method.type);
                                                }}
                                                type="primary"
                                                size="medium"
                                                disabled={moveInLoading || pricingPending}
                                                style={{ marginBottom: '12px' }}
                                            >
                                                {moveInLoading ? (
                                                    <Spinner size="25" inverted />
                                                ) : (
                                                    <>
                                                        {confirmBookingButtonText(
                                                            method.custom_fields?.display_name,
                                                        )}
                                                    </>
                                                )}
                                            </Button>
                                        ));
                                    }
                                    return (
                                        <Button
                                            disableScroll
                                            onclick={processBooking}
                                            type="primary"
                                            size="medium"
                                            disabled={moveInLoading || pricingPending}
                                        >
                                            {moveInLoading ? (
                                                <Spinner size="25" inverted />
                                            ) : (
                                                <>{confirmBookingButtonText()}</>
                                            )}
                                        </Button>
                                    );
                                })()}

                                {(window.rapidstor_settings.rapidstor_options.dev
                            || location.administrative.testMode) && (
                            <Button
                                style={{ marginTop: '12px' }}
                                onclick={fillForm}
                                disableScroll
                                type="outlineOnBackground"
                                size="medium"
                            // eslint-disable-next-line i18next/no-literal-string
                            >
                                Fill Form
                            </Button>
                        )}
                            </div>

                            <ProductRemovalModal
                                show={showProductRemovalModal}
                                setShow={(value) => {
                            if (!value) {
                                setProductRemoval(null);
                            }
                        }}
                                item={productRemoval}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
}
